import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@proref/schemas';
import { notifyError } from '../lib/toast';
import { forgotPassword } from '../services/auth.service';
import { Logo } from '../components/shared/Logo';
import { Icon } from '../components/shared/Icons';
import { Input } from '../components/shared/Input';
import { Field } from '../components/shared/Field';
import { Button } from '../components/shared/Button';
import { Spinner } from '../components/shared/Spinner';

export function ForgotPasswordPage() {
  // The API always 202s (no account enumeration), so on success we show the same neutral
  // "check your email" state whether or not the address is registered.
  const [sentTo, setSentTo] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await forgotPassword(values);
      setSentTo(values.email);
    } catch (err) {
      // Network/rate-limit failure is the only thing that surfaces here — a missing account
      // is not an error (still 202). Toast it; inline field errors stay inline.
      notifyError(err, 'Could not send the reset link');
    }
  });

  return (
    <div className="grid min-h-screen place-items-center bg-bg p-6">
      <div className="w-full max-w-[400px]">
        <div className="mb-7 flex justify-center">
          <Logo size={22} />
        </div>

        {sentTo ? (
          // Neutral confirmation — deliberately does not confirm the account exists.
          <div className="rounded border border-line bg-surface p-6 text-center shadow-1">
            <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-sm bg-accent-tint text-accent">
              <Icon.mail className="w-[18px] h-[18px]" />
            </div>
            <h2 className="text-lg text-ink">Check your email</h2>
            <p className="mt-1.5 text-[13px] text-muted">
              If <span className="font-medium text-ink">{sentTo}</span> has a ProRef account, a
              password-reset link is on its way. The link expires in 1 hour.
            </p>
            <Link to="/login" className="mt-4 inline-block text-[13px] font-medium text-accent">
              Back to sign in
            </Link>
          </div>
        ) : (
          <div className="rounded border border-line bg-surface p-6 shadow-1">
            <h2 className="text-xl text-ink">Reset your password</h2>
            <p className="mb-5 mt-1 text-[13px] text-muted">
              Enter the email on your ProRef account and we'll send you a reset link.
            </p>

            <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
              <Field label="Email" htmlFor="email" error={errors.email?.message}>
                <Input id="email" type="email" autoComplete="email" placeholder="marko@hrs.hr" {...register('email')} />
              </Field>

              <Button type="submit" variant="accent" size="lg" block disabled={isSubmitting} className="mt-1">
                {isSubmitting ? <Spinner className="w-4 h-4" /> : <>Send reset link <Icon.arrow className="w-3.5 h-3.5" /></>}
              </Button>
            </form>

            <p className="mt-4 text-center text-[12px] text-muted">
              Remembered it?{' '}
              <Link to="/login" className="font-medium text-accent">
                Sign in
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
